import React, { Component, ReactNode, Suspense } from 'react';
import { compose } from 'recompose';
import { Trans } from 'react-i18next';
import { MutationFunction } from 'react-apollo';
import { Grid, Link, Typography } from '@material-ui/core';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import Linkify from 'react-linkify';

import { updateCommentResponse } from 'apollo/responses';
import { Comment, PublicUserProfile, UpdateCommentComponent } from 'api';
import { IWithErrorHandler, IWithQueryParams, withErrorHandler, withQueryParams } from 'providers';
import { Editor, EditorFormat, EditorModes, Error, UserAvatar } from 'components';
import { getFullName } from 'helpers';
import { SPLIT_MENTION_REGEX } from 'constants/index';
import { generateLink } from 'Links';

import editorStyles from './styles';
import Actions from './Actions';

interface IProps {
  comment: Comment;
  onReply: () => void;
  isReplying: boolean;
}

interface IPropsEnhanced
  extends IProps,
    IWithErrorHandler,
    IWithQueryParams,
    WithStyles<typeof styles> {}

interface IState {
  isEditing: boolean;
  mentionedUsers: PublicUserProfile[];
}

const styles = (theme: Theme) =>
  createStyles({
    grid: {
      display: 'inline-grid',
    },
    box: {
      border: '0.5px solid transparent',
      justifySelf: 'flex-start',
      padding: theme.spacing(1),
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.spacing(2),
      ...theme.mixins.breakWord,
    },
    creator: {
      whiteSpace: 'nowrap',
      marginRight: theme.spacing(0.5),
    },
    text: {
      '& a': {
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    ...editorStyles(theme),
  });

const enhancers = compose<IPropsEnhanced, IProps>(
  withErrorHandler,
  withQueryParams,
  withStyles(styles),
);

class CommentItem extends Component<IPropsEnhanced, IState> {
  state: IState;

  constructor(props: IPropsEnhanced) {
    super(props);

    this.state = {
      isEditing: false,
      mentionedUsers: props.comment.users_mentioned || [],
    };
  }

  handleAddMention = (user: PublicUserProfile) => {
    const { mentionedUsers } = this.state;

    // Override inclusion of __typename
    const formattedUser: any = [
      {
        id: user.id,
        full_name: user.full_name,
        __typename: 'PublicUserProfile',
      },
    ];

    this.setState({ mentionedUsers: mentionedUsers.concat(formattedUser) });
  };

  getUser = (match: string) => {
    const { mentionedUsers } = this.state;
    return mentionedUsers.find(mentionedUser => mentionedUser.id === match);
  };

  toggleEdit = () => {
    const { isEditing } = this.state;
    this.setState({ isEditing: !isEditing });
  };

  onSubmit = (updateComment: MutationFunction) => (value: string) => {
    const { comment } = this.props;
    const { mentionedUsers } = this.state;

    const variables = {
      id: comment.id,
      comment_text: value,
      object_id: comment.object_id,
    };

    comment.users_mentioned = mentionedUsers;
    this.setState({ isEditing: false });

    return updateComment({
      variables,
      optimisticResponse: updateCommentResponse({ ...comment, comment_text: value }),
    });
  };

  renderEditor() {
    const { comment, classes, onError } = this.props;
    const { users_mentioned } = comment;

    return (
      <UpdateCommentComponent onError={onError}>
        {updateComment => (
          <Suspense fallback={this.generateComment()}>
            <Editor
              focusOnMount
              submitOnEnter
              disableToolbar
              format={EditorFormat.text}
              className={classes.editor}
              placeholder={<Trans i18nKey="content:addComment" />}
              mode={EditorModes.edit}
              mentions={users_mentioned}
              text={comment.comment_text}
              onAddMention={this.handleAddMention}
              onEscape={this.toggleEdit}
              onSubmit={this.onSubmit(updateComment)}
            />
          </Suspense>
        )}
      </UpdateCommentComponent>
    );
  }

  generateComment() {
    const { comment } = this.props;
    const { comment_text, has_substitutions } = comment;
    const creator = (
      <Link variant="body2" component={generateLink(`/profile/${comment.user.id}`)}>
        {getFullName(comment.user)}
      </Link>
    );

    if (!has_substitutions) {
      return this.renderComment(creator, comment_text);
    }

    const commentParts = comment_text.split(SPLIT_MENTION_REGEX);

    for (let i = 1, length = commentParts.length; i < length; i += 2) {
      const userId = commentParts[i];
      const user = this.getUser(userId);
      const fullName = getFullName(user);

      if (!user) {
        commentParts[i] = fullName;
      } else {
        // @ts-ignore TS2322
        commentParts[i] = (
          <Link color="primary" component={generateLink(`/profile/${user.id}`)} key={i}>
            {fullName}
          </Link>
        );
      }
    }

    return this.renderComment(creator, commentParts);
  }

  renderComment(creator: ReactNode, text: string | string[]) {
    const { classes } = this.props;

    return (
      <div className={classes.box}>
        <Typography>
          <span className={classes.creator}>{creator}</span>
          <span className={classes.text}>
            <Linkify properties={{ target: '_blank' }}>{text}</Linkify>
          </span>
        </Typography>
      </div>
    );
  }

  render() {
    const { classes, comment, error, isReplying, onReply, onErrorClose } = this.props;
    const { isEditing } = this.state;

    const width = isEditing ? '100%' : 'auto';

    return (
      <Grid container spacing={1}>
        <Error key={error} message={error} onClose={onErrorClose} />
        <Grid item>
          <UserAvatar link user={comment.user} />
        </Grid>
        <Grid item xs>
          <div style={{ width }} className={classes.grid}>
            {isEditing ? this.renderEditor() : this.generateComment()}
            <Actions
              comment={comment}
              isEditing={isEditing}
              isReplying={isReplying}
              onEdit={this.toggleEdit}
              onReply={onReply}
            />
          </div>
        </Grid>
      </Grid>
    );
  }
}

export default enhancers(CommentItem);
